import {createUseStyles} from 'react-jss'
import React, { useEffect } from "react";
import {titleMarginBottom} from "../constants/layout";
import usePayRunDashboardActions from "../hooks/usePayRunDashboardActions";
import CardSection from "../components/CardSection";
import Card from "../components/Card";
import {Colors} from "../constants/colors";
import EmployerPayRunList from "../components/PayRunDashboard/EmployerPayRunList";
import PayRunList from "../components/PayRunDashboard/PayRunList";
import {Spinner} from "react-bootstrap";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faInfoCircle, faMoneyBill, faReceipt, faRetweet, faSnowflake, faStop, faCircle} from '@fortawesome/free-solid-svg-icons'
import usePayRunDashboardAutoUpdate from "../components/PayRunDashboard/usePayRunDashboardAutoUpdate";
import {Toolbar, ToolbarItem} from "../components/Learning/Toolbar";
import Divider from "../components/Divider";
import {formatMoney} from "../utils/moneyHelper";
import {capitalize} from "../utils/stringHelper";
import useDidUpdateEffect from '../hooks/useDidUpdateEffect';
import { useAppSelector } from '../redux/hooks';
import useSwishStatus from "../hooks/useSwishStatus";
import { PayRunPayoutAttemptState, PayRunPayoutAttempt, PayTransferState } from '../redux/types';
import usePayoutSystemStatus, {getPayoutSystemSeverityColor} from "../hooks/usePayoutSystemStatus";

const getLastPayoutAttemptDisplay = (payoutAttempts: PayRunPayoutAttempt[]) => {
  if (!payoutAttempts || payoutAttempts.length === 0) {
    return '-';
  }

  const lastAttempt = payoutAttempts[payoutAttempts.length - 1];
  const state = capitalize(lastAttempt.state);

  if (lastAttempt.errorMessage && lastAttempt.state === PayRunPayoutAttemptState.Failed) {
    const truncatedError = lastAttempt.errorMessage.length > 30
      ? lastAttempt.errorMessage.substring(0, 30) + '...'
      : lastAttempt.errorMessage;
    return `${state}: ${truncatedError}`;
  }

  return state;
};

const PayRunDashboard = () => {
  const styles = useStyles()

  const incompleteEmployerPayRunPage = useAppSelector(state => state.payRunDashboard.incompleteEmployerPayRunPage)
  const incompletePayRunsPage = useAppSelector(state => state.payRunDashboard.incompletePayRuns)
  const frozenPayRunsPage = useAppSelector(state => state.payRunDashboard.frozenPayRuns)
  const swishStatus = useSwishStatus()
  const payoutSystemStatus = usePayoutSystemStatus()

  const {
    getIncompleteEmployerPayRuns,
    getIncompletePayRuns,
    getFrozenPayRuns,
    setIncompletePayRunsFilter,
    setIncompletePayRunsPageIndex,
    setFrozenPayRunsFilter,
    setFrozenPayRunsPageIndex,
  } = usePayRunDashboardActions()

  const {
    autoUpdateInterval,
    setAutoUpdateInterval
  } = usePayRunDashboardAutoUpdate()

    // Fetch the incomplete pay runs and frozen pay runs when the component mounts
    useEffect(() => {
      if (incompleteEmployerPayRunPage.employerPayRuns.length === 0 && !incompleteEmployerPayRunPage.loading) {
        getIncompleteEmployerPayRuns()
      }

      if (incompletePayRunsPage.payRuns.length === 0 && !incompletePayRunsPage.loading) {
        getIncompletePayRuns(
          incompletePayRunsPage.searchTerm,
          incompletePayRunsPage.pageIndex,
          incompletePayRunsPage.pageSize
        )
      }

      if (frozenPayRunsPage.payRuns.length === 0 && !frozenPayRunsPage.loading) {
        getFrozenPayRuns(
          frozenPayRunsPage.searchTerm,
          frozenPayRunsPage.pageIndex,
          frozenPayRunsPage.pageSize
        )
      }
    }, []);

    // When the incomplete pay runs page index or search term changes, fetch the incomplete pay runs
    useDidUpdateEffect(() => {
      getIncompletePayRuns(
        incompletePayRunsPage.searchTerm,
        incompletePayRunsPage.pageIndex,
        incompletePayRunsPage.pageSize
      )
    }, [incompletePayRunsPage.pageIndex, incompletePayRunsPage.searchTerm])

    // When the frozen pay runs page index or search term changes, fetch the frozen pay runs
    useDidUpdateEffect(() => {
      getFrozenPayRuns(
        frozenPayRunsPage.searchTerm,
        frozenPayRunsPage.pageIndex,
        frozenPayRunsPage.pageSize
      )
    }, [frozenPayRunsPage.pageIndex, frozenPayRunsPage.searchTerm])

    return (
    <div>
      <div className={styles.pageHeader}>
        <h1>Ongoing pay runs</h1>

        <Toolbar label={"Auto update"}>
          <ToolbarItem icon={faStop} title={"Off"} display={"iconAndText"}
                       disabled={!autoUpdateInterval}
                       onClick={() => {
                         setAutoUpdateInterval(null)
                       }}/>
          <ToolbarItem icon={faRetweet} title={"5s"} display={"iconAndText"}
                       disabled={autoUpdateInterval === 5}
                       onClick={() => {
                         setAutoUpdateInterval(5)
                       }}/>
          <ToolbarItem icon={faRetweet} title={"1m"} display={"iconAndText"}
                       disabled={autoUpdateInterval === 60}
                       onClick={() => {
                         setAutoUpdateInterval(60)
                       }}/>
        </Toolbar>
      </div>


      <CardSection>

        <Card className={`${styles.card} card`}>
          <div className={styles.cardHeader}>
            <h3>Employer pay runs ({incompleteEmployerPayRunPage.employerPayRuns.length})</h3>
            {!incompleteEmployerPayRunPage.loading &&
                <FontAwesomeIcon icon={faReceipt} size="2x" color={Colors.white}/>}
            {incompleteEmployerPayRunPage.loading && <Spinner animation={"border"}/>}
          </div>
          <EmployerPayRunList employerPayRunPage={incompleteEmployerPayRunPage}/>
        </Card>

        <Card className={`${styles.card} card`}>
          <div className={styles.cardHeader}>
            <h3>Frozen pay runs ({frozenPayRunsPage.totalCount})</h3>
            {!frozenPayRunsPage.loading && <FontAwesomeIcon icon={faSnowflake} size="2x" color={Colors.white}/>}
            {frozenPayRunsPage.loading && <Spinner animation={"border"}/>}
          </div>
          <PayRunList
            payRunPage={frozenPayRunsPage}
            onFilterChange={setFrozenPayRunsFilter}
            onPageChange={setFrozenPayRunsPageIndex}
            columns={[
              {
                id: 'name',
                header: 'Name',
                accessorFn: (row) => `${row.userGivenName ?? '-'} ${row.userFamilyName ?? '-'}`,
              },
              {
                id: 'employer',
                header: 'Employer',
                accessorFn: (row) => row.employerName ?? '-',
              },
              {
                id: 'payBefore',
                header: 'Pay (before)',
                accessorFn: (row) => formatMoney(row.originalSalary),
              },
              {
                id: 'payAfter',
                header: 'Pay (after)',
                accessorFn: (row) => formatMoney(row.salaryAfterDeductions),
              },
            ]}
          />
        </Card>
      </CardSection>

      <Divider height={10}/>

      <Card className={`${styles.card} card`}>
        <div className={styles.cardHeader}>
          <h3>Incomplete pay payouts ({incompletePayRunsPage.totalCount})</h3>
          {!incompletePayRunsPage.loading && <FontAwesomeIcon icon={faMoneyBill} size="2x" color={Colors.white}/>}
          {incompletePayRunsPage.loading && <Spinner animation={"border"}/>}
        </div>
        <PayRunList
          payRunPage={incompletePayRunsPage}
          onFilterChange={setIncompletePayRunsFilter}
          onPageChange={setIncompletePayRunsPageIndex}
          columns={[
            {
              id: 'name',
              header: 'Name',
              accessorFn: (row) => `${row.userGivenName ?? '-'} ${row.userFamilyName ?? '-'}`,
            },
            {
              id: 'employer',
              header: 'Employer',
              accessorFn: (row) => row.employerName ?? '-',
            },
            {
              id: 'payBefore',
              header: 'Pay (before)',
              accessorFn: (row) => formatMoney(row.originalSalary),
            },
            {
              id: 'payAfter',
              header: 'Pay (after)',
              accessorFn: (row) => formatMoney(row.salaryAfterDeductions),
            },
            {
              id: 'payTransferState',
              header: 'Pay Transfer State',
              accessorFn: (row) => capitalize(row.payTransferState == PayTransferState.None 
                ? row.state 
                : row.payTransferState),
            },
            {
              id: 'lastPayoutAttemptState',
              header: 'Last payout attempt',
              accessorFn: (row) => getLastPayoutAttemptDisplay(row.payoutAttempts)
            }
          ]}
        />
      </Card>

      <Divider height={10}/>

      <Card className={`${styles.card} card`}>
        <div className={styles.cardHeader}>
          <h3>Swish status</h3>
          <FontAwesomeIcon icon={faInfoCircle} size="2x" color={Colors.white}/>
        </div>

        <h4>Reported by Cappy</h4>
        <div className={styles.swishStatus}>
          <FontAwesomeIcon icon={faCircle} color={getPayoutSystemSeverityColor(payoutSystemStatus?.severity)} />
          <span>{capitalize(payoutSystemStatus?.severity || 'unknown')}</span>
        </div>
        {payoutSystemStatus?.isBankSpecific && payoutSystemStatus?.banks?.length > 0 && (
          <div>
            <span>Banks: {payoutSystemStatus.banks.join(', ')}</span>
          </div>
        )}

        <h4>Reported by Swish</h4>

        <div className={styles.swishStatus}>
          <FontAwesomeIcon icon={faCircle} color={swishStatus?.status.color || Colors.white}/>
          <span>{swishStatus?.status.description}</span>
        </div>
        <div style={{alignSelf: "flex-end"}}>
          <a href="https://status.swish.nu/" target="_blank" rel="noopener noreferrer">
            View Swish status page
          </a>
        </div>
      </Card>

      <Divider height={10}/>

      <Card className={`${styles.card} card`}>
        <div className={styles.cardHeader}>
          <h3>Info</h3>
          <FontAwesomeIcon icon={faInfoCircle} size="2x" color={Colors.white}/>
        </div>
        <h6>Pause pay run</h6>
        <p>
          Pause an ongoing pay transfer for a pay run. This is useful when a PayRun fails to be
          completed for some reason, and the system keeps creating new PayoutAttempts with no
          chance of success. The PayRun must be in the PayoutPending state to be paused.
        </p>
        <p>
          Depending on timing, a new PayoutAttempt may be created after the PayRun is paused,
          and this is fine. The paused PayRun/PayoutAttempt will not be processed by the PayPayoutProcessor
          until it is resumed.
        </p>

        <h6>Manually complete pay run</h6>
        <p>
          In some cases, a PayRun can not be completed by the system. For example, this might occur
          if a user has no valid Swish number or recipient bank account. In these cases, the wages/salary
          will have to be returned to the employer manually for further processing. In order to be able
          to close out the PayRun, the pay transfer must be marked as completed manually.
        </p>
        <p>
          The initiator of the manual completion must ensure that the funds are returned to the
          employer, and that they are informed - the system will consider the pay transfer of this PayRun
          completed.
        </p>
      </Card>
    </div>
  )
}

export default PayRunDashboard

const useStyles = createUseStyles({
  pageHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: titleMarginBottom
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 400,
    padding: 25,
    backgroundColor: Colors.darkGrayCard,
    borderColor: Colors.darkGrayBorder,
    borderWidth: 2,
    borderRadius: 10
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 10,
  },
  swishStatus: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  }
})
